import { useState } from 'react';
import { Link } from 'react-router-dom';
import { rbac } from '@dha-nexus/shared';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';
import { useClientPagination } from '../../lib/usePagination.js';
import { PageHeader, EmptyState } from '../../components/index.js';
import { PageViewerControls } from '../../components/primitives.jsx';
import './me.css';
import './me.redesign.css';

// Organization ids like ORG-DHA-STRAT-EXEC are the only org fact that reaches
// the client today (there is no organizations lookup API). We NEVER invent an
// org name — we humanize the id (title-case the tokens, keep well-known
// acronyms uppercase) and always keep the raw id visible as secondary text.
const ORG_ACRONYMS = new Set(['DHA', 'DHMS', 'MHS', 'AI', 'IT', 'PMO', 'PEO', 'EVM', 'US', 'RAPID']);

function titleCaseTokens(value, acronyms = null) {
  return String(value || '')
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) =>
      acronyms && acronyms.has(word.toUpperCase())
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(' ');
}

export function humanizeOrgId(orgId) {
  const raw = String(orgId || '').replace(/^ORG-/i, '');
  if (!raw) return '';
  return titleCaseTokens(raw, ORG_ACRONYMS);
}

// Role id -> user-facing persona name. The canonical mapping lives in shared
// rbac (PERSONA_DISPLAY, keyed by persona key). Roles that have a persona entry
// use its label; roles without one fall back to a humanized role id. We never
// duplicate the shared mapping — only humanize what it does not cover.
export function personaLabelForRole(roleId) {
  const key = String(roleId || '')
    .replace(/^ROLE-/i, '')
    .toLowerCase()
    .replace(/-/g, '_');
  const label = rbac.PERSONA_DISPLAY?.[key]?.label;
  if (label) return label;
  return titleCaseTokens(String(roleId || '').replace(/^ROLE-/i, '')) || roleId || '—';
}

// Assignment posture derivable from data the client already holds. The API
// filters pending role grants out of the effective role set, so an authentic
// pending session has zero active roles but retains its pending roleAssignments
// on the user payload. Expired/suspended/revoked have no data source today
// (documented in WORKER_RESULT.md) and are deliberately not invented here.
export function deriveAssignmentPosture({ roles, roleAssignments }) {
  const activeRoles = Array.isArray(roles) ? roles : [];
  const assignments = Array.isArray(roleAssignments) ? roleAssignments : [];
  const pending = assignments.filter(
    (assignment) => String(assignment?.status || '').toLowerCase() === 'pending',
  );
  if (activeRoles.length > 0) return { posture: 'active', pending };
  if (pending.length > 0) return { posture: 'pending', pending };
  return { posture: 'none', pending };
}

const POSTURE_COPY = {
  active: 'Active access',
  pending: 'Access pending approval',
  none: 'No active access',
};

export function MyProfilePage() {
  const { user, roles, permissions, defaultLanding } = useCurrentUser();
  const [personasOpen, setPersonasOpen] = useState(false);

  const permList = Array.isArray(permissions) ? permissions : [];
  const permGroups = permList.reduce((acc, p) => {
    const raw = String(p).replace(/^PERM-/i, '');
    const key = (raw.split(/[-._:]/)[0] || 'general').toLowerCase();
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {});
  const groupEntries = Object.entries(permGroups).sort((a, b) => b[1] - a[1]);
  const maxGroup = groupEntries.length ? Math.max(...Object.values(permGroups)) : 0;

  const activeRoles = Array.isArray(roles) ? roles : [];
  const personaEntries = activeRoles.map((roleId) => ({
    roleId,
    name: personaLabelForRole(roleId),
  }));

  const personaPager = useClientPagination(personaEntries, {
    initialPageSize: 6,
    resetKey: user?.id || 'anonymous',
  });
  const permissionPager = useClientPagination(groupEntries, {
    initialPageSize: 8,
    resetKey: user?.id || 'anonymous',
  });

  if (!user) return <EmptyState title="Not signed in" />;

  const initials = (user.displayName || '?')
    .split(' ')
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const rawOrgId = user.homeOrganizationId || '';
  const orgLabel = humanizeOrgId(rawOrgId);
  const landingRoute = defaultLanding || user.defaultLandingRoute || '/home';
  const { posture, pending } = deriveAssignmentPosture({
    roles: activeRoles,
    roleAssignments: user.roleAssignments,
  });

  return (
    <>
      <PageHeader
        eyebrow="Me"
        title="Profile"
        subtitle="Who you are, where you're assigned, and what access you hold."
      />

      {/* a. Identity hero — name, title, primary organization, assignment state.
          Raw user id demoted to secondary text (AC-01). */}
      <section className="me-profile me-hero" data-testid="profile-hero">
        <div className="me-avatar" aria-hidden>
          {initials}
        </div>
        <div className="me-hero__body">
          <h2 className="me-hero__name">{user.displayName}</h2>
          <p className="me-hero__title">{user.title || 'DHA PRIME user'}</p>

          <div className="me-hero__facts">
            <span
              className="me-hero__org me-truncate"
              data-testid="profile-hero-org"
              title={rawOrgId || undefined}
            >
              {orgLabel || 'Organization not set'}
            </span>
            <span className="me-hero__email me-truncate" title={user.email || undefined}>
              {user.email || 'No email on file'}
            </span>
          </div>

          <div className="me-hero__meta">
            <span
              className={`me-state me-state--${posture}`}
              data-testid="profile-assignment-state"
              data-posture={posture}
            >
              <span className="me-state__dot" aria-hidden />
              {POSTURE_COPY[posture]}
            </span>
            <span className="me-hero__id mono" data-testid="profile-hero-id">
              {user.id}
            </span>
          </div>
        </div>
      </section>

      {/* b. Quick links immediately below the hero (AC-03). */}
      <section className="me-redesign-section card me-quicklinks" data-testid="profile-quicklinks">
        <h2 className="card-title">Quick links</h2>
        <p className="card-subtitle">Where you spend most of your time.</p>
        <ul className="me-link-list">
          <li>
            <Link to={landingRoute}>Your default landing →</Link>
          </li>
          <li>
            <Link to="/notifications">Inbox & alerts →</Link>
          </li>
          <li>
            <Link to="/saved-views">Saved views →</Link>
          </li>
          <li>
            <Link to="/exports">Briefings & exports →</Link>
          </li>
          <li>
            <Link to="/settings">Preferences →</Link>
          </li>
        </ul>
      </section>

      {/* c. Summary cards — Personas, Primary scope, Matrix access, Assigned
          resources, and Pending access (only when relevant). Each reveals its
          underlying records or links to the surface that owns the concept. */}
      <section className="me-redesign-section" aria-labelledby="me-summary-heading">
        <h2 id="me-summary-heading" className="me-section-heading">
          Access & assignment
        </h2>
        <div className="me-summary-grid">
          {/* Personas — expands to the persona list (AC-02, AC-06). */}
          <div className="card me-summary-card" data-testid="profile-card-personas">
            <h3 className="card-title">Personas</h3>
            <p className="card-subtitle">
              {personaEntries.length} active persona{personaEntries.length === 1 ? '' : 's'} on your
              principal.
            </p>
            {personaEntries.length === 0 ? (
              <p className="muted small">
                {posture === 'pending'
                  ? 'No active personas yet — your access is pending approval.'
                  : 'No personas are attached to your account.'}
              </p>
            ) : (
              <>
                <button
                  type="button"
                  className="me-expander"
                  data-testid="profile-personas-toggle"
                  aria-expanded={personasOpen}
                  aria-controls="profile-personas-list"
                  onClick={() => setPersonasOpen((open) => !open)}
                >
                  {personasOpen
                    ? 'Hide personas'
                    : `Show ${personaEntries.length} persona${
                        personaEntries.length === 1 ? '' : 's'
                      }`}
                </button>
                {personasOpen ? (
                  <>
                    <ul
                      className="me-persona-list"
                      id="profile-personas-list"
                      data-testid="profile-personas-list"
                    >
                      {personaPager.items.map((persona) => (
                        <li key={persona.roleId} className="me-persona-item">
                          <span className="me-persona-name">{persona.name}</span>
                          <span className="mono small me-persona-role me-truncate" title={persona.roleId}>
                            {persona.roleId}
                          </span>
                        </li>
                      ))}
                    </ul>
                    <PageViewerControls
                      page={personaPager.page}
                      label="personas"
                      pageSize={personaPager.pageSize}
                      onOffsetChange={personaPager.setOffset}
                      onPageSizeChange={personaPager.setPageSize}
                    />
                  </>
                ) : null}
              </>
            )}
          </div>

          {/* Primary scope — organizational anchor + landing workspace (AC-05/06). */}
          <div className="card me-summary-card" data-testid="profile-card-scope">
            <h3 className="card-title">Primary scope</h3>
            <p className="card-subtitle">Where your account is anchored.</p>
            <dl className="me-scope-dl">
              <div>
                <dt>Organization</dt>
                <dd className="me-truncate" title={rawOrgId || undefined}>
                  {orgLabel || '—'}
                </dd>
              </div>
              <div>
                <dt>Home organization id</dt>
                <dd className="mono small me-truncate" title={rawOrgId || undefined}>
                  {rawOrgId || '—'}
                </dd>
              </div>
              <div>
                <dt>Lands on</dt>
                <dd className="mono small">{landingRoute}</dd>
              </div>
            </dl>
          </div>

          {/* Matrix access — no data source today. Honest-empty, inert card that
              links to the surface that owns access reviews (AC-05). */}
          <div
            className="card me-summary-card me-summary-card--inert"
            data-testid="profile-card-matrix"
          >
            <h3 className="card-title">Matrix access</h3>
            <p className="card-subtitle">Cross-organization / matrixed grants.</p>
            <p className="muted small">
              Matrix access is not available in PRIME today. Your profile can’t show matrixed grants
              until the personnel-data integration lands.
            </p>
            <Link className="me-inline-link" to="/access/status">
              Review your access status →
            </Link>
          </div>

          {/* Assigned resources — no per-person assignment feed today. Honest-empty,
              inert card linking to the Resources surface (AC-05). */}
          <div
            className="card me-summary-card me-summary-card--inert"
            data-testid="profile-card-resources"
          >
            <h3 className="card-title">Assigned resources</h3>
            <p className="card-subtitle">Projects and allocations assigned to you.</p>
            <p className="muted small">
              No assigned resources are surfaced to your profile yet. PRIME doesn’t expose a
              per-person assignment feed today.
            </p>
            <Link className="me-inline-link" to="/resources">
              Open Resources →
            </Link>
          </div>

          {/* Pending access — only when the posture is pending (req 3). Surfaces
              the pending persona names honestly and links to access status. */}
          {posture === 'pending' ? (
            <div
              className="card me-summary-card me-summary-card--pending"
              data-testid="profile-card-pending"
            >
              <h3 className="card-title">Pending access</h3>
              <p className="card-subtitle">Requests awaiting approval.</p>
              <ul className="me-pending-list">
                {pending.map((assignment, index) => (
                  <li key={assignment.roleId || index} className="me-persona-item">
                    <span className="me-persona-name">{personaLabelForRole(assignment.roleId)}</span>
                    <span className="mono small me-persona-role me-truncate" title={assignment.roleId}>
                      {assignment.roleId}
                    </span>
                  </li>
                ))}
              </ul>
              <Link className="me-inline-link" to="/access/status">
                Review your access status →
              </Link>
            </div>
          ) : null}
        </div>
      </section>

      {/* d. One permission visualization only (AC-04) — the grouped bar list,
          which reveals the underlying per-domain counts. */}
      <section className="me-redesign-section">
        <div className="card" data-testid="profile-perm-domains-card">
          <h2 className="card-title">Permission domains</h2>
          <p className="card-subtitle">
            {permList.length} permission{permList.length === 1 ? '' : 's'} grouped by domain — where
            your granted access concentrates.
          </p>
          {groupEntries.length === 0 ? (
            <p className="muted small">No permissions resolved for the current session.</p>
          ) : (
            <>
              <ul className="me-perm-list" data-testid="profile-perm-domains">
                {permissionPager.items.map(([group, count]) => (
                  <li key={group}>
                    <span className="me-perm-group">{group}</span>
                    <span className="me-perm-bar" aria-hidden>
                      <span
                        className="me-perm-bar-fill"
                        style={{ width: `${Math.min(100, (count / (maxGroup || 1)) * 100)}%` }}
                      />
                    </span>
                    <span className="mono small">{count}</span>
                  </li>
                ))}
              </ul>
              <PageViewerControls
                page={permissionPager.page}
                label="permission domains"
                pageSize={permissionPager.pageSize}
                onOffsetChange={permissionPager.setOffset}
                onPageSizeChange={permissionPager.setPageSize}
              />
            </>
          )}
        </div>
      </section>
    </>
  );
}
